import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { PERMISSIONS } from "../utils/permissionCodes";
import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from "../utils/permissions";
import { isAdminRole } from "../utils/roles";
import { AUTH_SESSION_CHANGE_EVENT, readAuthSession } from "../utils/session";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readAuthSession());

  const refreshSession = useCallback(() => {
    setSession(readAuthSession());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleSessionChange = () => {
      refreshSession();
    };

    window.addEventListener(AUTH_SESSION_CHANGE_EVENT, handleSessionChange);

    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGE_EVENT, handleSessionChange);
    };
  }, [refreshSession]);

  const permissions = session.user.permissions;
  const contactId = session.contactId;
  const company = session.company;
  const role = session.user.role;

  const isAdmin = useMemo(() => {
    if (contactId != null) return false;
    return (
      isAdminRole(role) || hasPermission(permissions, PERMISSIONS.USERS_VIEW)
    );
  }, [contactId, permissions, role]);

  const value = useMemo(
    () => ({
      ...session,
      permissions,
      contactId,
      company,
      isAdmin,
      refreshSession,
      hasPermission: (permissionCode) =>
        hasPermission(permissions, permissionCode),
      hasAnyPermission: (permissionCodes) =>
        hasAnyPermission(permissions, permissionCodes),
      hasAllPermissions: (permissionCodes) =>
        hasAllPermissions(permissions, permissionCodes),
    }),
    [company, contactId, isAdmin, permissions, refreshSession, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
